import { PointerEvent, PointerEventHandler, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { ChevronLeftIcon, ChevronRightIcon } from 'app/agriculture/icons';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';
import {
    useTimelineInterval,
    useTimelineCursorTime,
    useTimelineDispatch,
} from 'app/agriculture/AgTimeline/context/TimelineContext';
import { percentageToDate } from './utils/percentageToDate';
import { calculateMovePosition } from './utils/calculateMovePosition';
import { snappedCursorTime } from './utils/snappedCursorTime';
import './TimelineScrubber.scss';
import { percentageOfInterval } from 'app/agriculture/AgTimeline/utils/percentageOfInterval';

interface TimelineScrubberProps {
    condensed?: boolean;
}

export function TimelineScrubber({ condensed = false }: TimelineScrubberProps) {
    const timelineInterval = useTimelineInterval();
    const cursorTime = useTimelineCursorTime();
    const { setCursorTime } = useTimelineDispatch();
    const [pointerId, setPointerId] = useState<number | null>();
    const ref = useRef<HTMLDivElement>(null);

    const handlePointerDown = useCallback((event: PointerEvent) => {
        if (ref.current) {
            ref.current.setPointerCapture(event.pointerId);
            ref.current.style.cursor = 'grabbing';
            setPointerId(event.pointerId);
        }
    }, []);

    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            if (ref.current && pointerId) {
                const newPosition = calculateMovePosition(event);
                if (newPosition) {
                    const newCursorTime = snappedCursorTime(
                        percentageToDate(newPosition, timelineInterval),
                        timelineInterval,
                    );
                    setCursorTime(newCursorTime);
                }
            }
        },
        [setCursorTime, pointerId, timelineInterval],
    );

    const handlePointerUp = useCallback(() => {
        if (ref.current && pointerId) {
            ref.current.releasePointerCapture(pointerId);
            ref.current.style.cursor = 'grab';
            setPointerId(null);
        }
    }, [pointerId]);

    const handlePointerLeave = useCallback(() => {
        if (ref.current && pointerId && !ref.current.hasPointerCapture(pointerId)) {
            setPointerId(null);
        }
    }, [pointerId]);

    const throttledNow = useThrottledNow();
    const position = useMemo(() => {
        const percentage = percentageOfInterval(cursorTime || throttledNow, timelineInterval) * 100;
        return `${percentage}%`;
    }, [cursorTime, timelineInterval, throttledNow]);

    return (
        <div className="TimelineScrubber__container">
            <div
                ref={ref}
                className={classNames('TimelineScrubber', {
                    'TimelineScrubber--condensed': condensed,
                })}
                style={{ '--timeline-position': position }}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerMove={handlePointerMove}
                onPointerLeave={handlePointerLeave}
            >
                <div className="TimelineScrubber__line" />
                <div className="TimelineScrubber__handle">
                    <ChevronLeftIcon size={22} />
                    <ChevronRightIcon size={22} />
                </div>
            </div>
        </div>
    );
}
